import { useState } from 'react';
import { AdminScreen, LoadOrError, adminErrorText, useResource } from './Overview';
import { Alert, Button, Card, Input, Modal, StatusPill, Textarea, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { brl } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface PlanRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  price_month_cents: number;
  price_year_cents: number;
  annual_discount_percent: number;
  max_professionals: number;
  active: number;
  sort_order: number;
}

interface FeatureRow {
  id: string;
  plan_id: string;
  feature_key: string;
  enabled: number;
  label: string | null;
}

interface PlansPayload {
  plans: PlanRow[];
  features: FeatureRow[];
  availableFeatures: string[];
}

interface PlanDraft {
  name: string;
  description: string;
  priceMonth: string;
  annualDiscount: string;
  maxProfessionals: string;
  active: boolean;
}

const FEATURE_LABEL: Record<string, string> = {
  online_booking: 'Reserva on-line pelo cliente',
  public_profile: 'Perfil público',
  agenda_daily: 'Agenda em dia',
  agenda_weekly: 'Agenda em semana',
  agenda_monthly: 'Agenda em mês',
  customer_management: 'Cadastro de clientes',
  history: 'Histórico de atendimentos',
  public_link: 'Link público compartilhável',
  whatsapp_shortcut: 'Atalho de WhatsApp',
  schedule_config: 'Configuração de horários',
  team: 'Equipe',
  individual_agendas: 'Agendas individuais',
  reports: 'Relatórios',
  blocked_periods: 'Períodos bloqueados',
  cancellation_history: 'Histórico de cancelamentos',
  report_export: 'Exportação de relatórios',
  extended_gallery: 'Portfólio ampliado',
  permissions: 'Permissões por membro',
  central_agenda: 'Agenda central',
  advanced_reports: 'Relatórios avançados',
  kpi: 'Indicadores (KPI)',
  services_per_professional: 'Serviços por profissional',
  business_settings: 'Configurações do estabelecimento'
};

function parseBrlToCents(input: string): number | null {
  const clean = input.replace(/[rR]\s?\$|\s/g, '').trim();
  if (!clean) return null;
  const normalized = clean.includes(',')
    ? clean.replace(/\./g, '').replace(',', '.')
    : clean.includes('.') && /\.\d{1,2}$/.test(clean)
      ? clean
      : clean.replace(/\./g, '');
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

function draftFromPlan(plan: PlanRow): PlanDraft {
  return {
    name: plan.name,
    description: plan.description ?? '',
    priceMonth: (plan.price_month_cents / 100).toFixed(2).replace('.', ','),
    annualDiscount: String(plan.annual_discount_percent),
    maxProfessionals: String(plan.max_professionals),
    active: plan.active === 1
  };
}

export function AdminPlansPage(): JSX.Element {
  usePageMeta('Planos — administração', 'Preços, limites e recursos de cada plano da AgendaPro.', '/admin/planos');

  const toast = useToast();
  const resource = useResource<PlansPayload>(() => api.get<PlansPayload>('/api/admin/plans'));
  const [drafts, setDrafts] = useState<Record<string, PlanDraft>>({});
  const [priceError, setPriceError] = useState<Record<string, string | null>>({});
  const [savingPlan, setSavingPlan] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [pendingFeature, setPendingFeature] = useState<{ plan: PlanRow; key: string; enabled: boolean } | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<PlanRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const plans = resource.data?.plans ?? [];
  const features = resource.data?.features ?? [];
  const available = resource.data?.availableFeatures ?? [];

  function draftFor(plan: PlanRow): PlanDraft {
    return drafts[plan.id] ?? draftFromPlan(plan);
  }

  function patchDraft(plan: PlanRow, patch: Partial<PlanDraft>): void {
    setDrafts((prev) => ({ ...prev, [plan.id]: { ...draftFor(plan), ...patch } }));
  }

  async function savePlan(plan: PlanRow): Promise<void> {
    const draft = draftFor(plan);
    const cents = parseBrlToCents(draft.priceMonth);
    if (cents === null) {
      setPriceError((prev) => ({ ...prev, [plan.id]: 'Use o formato R$ 29,90 (números, vírgula para centavos).' }));
      return;
    }
    const discount = Number(draft.annualDiscount);
    if (!Number.isInteger(discount) || discount < 0 || discount > 90) {
      setPriceError((prev) => ({ ...prev, [plan.id]: 'O desconto anual deve ser um número inteiro entre 0 e 90.' }));
      return;
    }
    const maxPros = Number(draft.maxProfessionals);
    if (!Number.isInteger(maxPros) || maxPros < 1 || maxPros > 500) {
      setPriceError((prev) => ({ ...prev, [plan.id]: 'O limite de profissionais deve ser um número inteiro entre 1 e 500.' }));
      return;
    }
    if (draft.name.trim().length < 2) {
      setPriceError((prev) => ({ ...prev, [plan.id]: 'O nome do plano deve ter ao menos 2 caracteres.' }));
      return;
    }
    if (draft.description.length > 300) {
      setPriceError((prev) => ({ ...prev, [plan.id]: 'A descrição deve ter no máximo 300 caracteres.' }));
      return;
    }

    setPriceError((prev) => ({ ...prev, [plan.id]: null }));
    setSavingPlan(plan.id);
    setActionError(null);
    try {
      // O backend recalcula o preço anual a partir do mensal + desconto, por isso ambos são enviados juntos.
      await api.patch<{ ok: boolean }>(`/api/admin/plans/${plan.id}`, {
        name: draft.name.trim(),
        description: draft.description.trim(),
        priceMonthCents: cents,
        annualDiscountPercent: discount,
        maxProfessionals: maxPros,
        active: draft.active
      });
      toast(tr('Plano {name} atualizado. Alteração registrada em Auditoria.', { name: draft.name }), 'success');
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[plan.id];
        return next;
      });
      await resource.reload();
    } catch (e) {
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setSavingPlan(null);
    }
  }

  async function toggleFeature(plan: PlanRow, featureKey: string, enabled: boolean): Promise<void> {
    setToggling(`${plan.id}:${featureKey}`);
    setActionError(null);
    try {
      await api.put<{ ok: boolean }>(`/api/admin/plans/${plan.id}/features/${featureKey}`, { enabled });
      toast(
        tr(enabled ? '{feature} ligada no plano {plan}.' : '{feature} desligada no plano {plan}.', {
          feature: tr(FEATURE_LABEL[featureKey] ?? featureKey),
          plan: plan.name
        }),
        'success'
      );
      setPendingFeature(null);
      await resource.reload();
    } catch (e) {
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setToggling(null);
    }
  }

  return (
    <AdminScreen
      title={tr("Planos e recursos")}
      description={tr("Preço mensal, desconto anual, limite de profissionais, disponibilidade e quais recursos cada plano libera. O preço anual é recalculado pelo backend.")}
    >
      <LoadOrError loading={resource.loading} error={resource.error} />

      {!resource.loading && plans.length === 0 && !resource.error ? (
        <EmptyPlans />
      ) : null}

      {actionError ? <div className="mb-4"><Alert tone="danger" title={tr("Não foi possível salvar")}>{actionError}</Alert></div> : null}

      <div className="grid gap-4">
        {plans.map((plan) => {
          const draft = draftFor(plan);
          const planFeatures = available.map((key) => ({
            key,
            row: features.find((f) => f.plan_id === plan.id && f.feature_key === key) ?? null
          }));
          const enabledCount = planFeatures.filter((item) => item.row?.enabled === 1).length;
          const error = priceError[plan.id] ?? null;

          return (
            <Card key={plan.id} as="section">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl">{plan.name}</h2>
                  <p className="nl-hint">{tr("Chave interna:")} <code>{plan.key}</code> {tr("· ordem")} {plan.sort_order}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill tone={plan.active ? 'positive' : 'neutral'}>
                    {plan.active ? tr('Plano publicado à venda') : tr('Plano inativo (não aparece em /precos)')}
                  </StatusPill>
                  <StatusPill tone="neutral">
                    {enabledCount} {tr("de")} {available.length} {tr("recursos ligados")}
                  </StatusPill>
                </div>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label className="nl-label" htmlFor={`name-${plan.id}`}>{tr("Nome do plano")}</label>
                  <Input
                    id={`name-${plan.id}`}
                    value={draft.name}
                    maxLength={80}
                    onChange={(e) => patchDraft(plan, { name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="nl-label" htmlFor={`price-${plan.id}`}>{tr("Preço mensal (R$)")}</label>
                  <Input
                    id={`price-${plan.id}`}
                    inputMode="decimal"
                    value={draft.priceMonth}
                    invalid={Boolean(error)}
                    onChange={(e) => patchDraft(plan, { priceMonth: e.target.value })}
                    placeholder="29,90"
                  />
                  <p className="nl-hint mt-1">{tr("Hoje:")} {brl(plan.price_month_cents)} {tr("por mês.")}</p>
                </div>
                <div>
                  <label className="nl-label" htmlFor={`discount-${plan.id}`}>{tr("Desconto anual (%)")}</label>
                  <Input
                    id={`discount-${plan.id}`}
                    inputMode="numeric"
                    value={draft.annualDiscount}
                    maxLength={2}
                    onChange={(e) => patchDraft(plan, { annualDiscount: e.target.value.replace(/\D/g, '') })}
                  />
                  <p className="nl-hint mt-1">
                    {tr("Valor anual atual:")} {brl(plan.price_year_cents)} {tr("— recalculado pelo backend como mensal × 12 × (1 − desconto).")}
                  </p>
                </div>
                <div>
                  <label className="nl-label" htmlFor={`max-${plan.id}`}>{tr("Limite de profissionais")}</label>
                  <Input
                    id={`max-${plan.id}`}
                    inputMode="numeric"
                    value={draft.maxProfessionals}
                    maxLength={3}
                    onChange={(e) => patchDraft(plan, { maxProfessionals: e.target.value.replace(/\D/g, '') })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="nl-label" htmlFor={`desc-${plan.id}`}>{tr("Descrição comercial")}</label>
                  <Textarea
                    id={`desc-${plan.id}`}
                    rows={2}
                    maxLength={300}
                    value={draft.description}
                    onChange={(e) => patchDraft(plan, { description: e.target.value })}
                  />
                  <p className="nl-hint mt-1">{draft.description.length}{tr("/300 caracteres.")}</p>
                </div>
                <div className="flex flex-col justify-end gap-2">
                  <label className="flex items-start gap-3 rounded-md border border-line px-3 py-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={draft.active}
                      onChange={(e) => {
                        if (!e.target.checked) setPendingDeactivate(plan);
                        else patchDraft(plan, { active: true });
                      }}
                    />
                    <span>
                      <span className="font-semibold">{tr("Plano ativo para novas assinaturas")}</span>
                      <span className="nl-hint block">
                        {tr("Desligar tira o plano da página de preços; quem já assina continua no plano atual.")}
                      </span>
                    </span>
                  </label>
                  <Button loading={savingPlan === plan.id} disabled={savingPlan === plan.id} onClick={() => void savePlan(plan)}>
                    {tr("Salvar plano")}
                  </Button>
                </div>
              </div>

              {error ? <div className="mt-3"><Alert tone="danger" title={tr("Verifique os valores")}>{error}</Alert></div> : null}

              <div className="mt-5 border-t border-line pt-4">
                <h3 className="font-display text-lg font-semibold">{tr("Recursos deste plano")}</h3>
                <p className="nl-hint mt-1">
                  {tr("Ligar ou desligar muda imediatamente o que o estabelecimento pode usar: o backend bloqueia rotas com")}{' '}
                  <code>{tr("feature_not_in_plan")}</code>.
                </p>
                <ul className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {planFeatures.map((item) => {
                    const enabled = item.row?.enabled === 1;
                    const id = `${plan.id}:${item.key}`;
                    return (
                      <li key={item.key} className="flex items-center justify-between gap-3 rounded-md border border-line px-3 py-2">
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{tr(FEATURE_LABEL[item.key] ?? item.row?.label ?? item.key)}</span>
                          <span className="nl-hint block truncate">{item.key}</span>
                        </span>
                        <Button
                          variant={enabled ? 'secondary' : 'primary'}
                          className="shrink-0 px-3 py-1.5 text-xs"
                          loading={toggling === id}
                          disabled={toggling !== null}
                          onClick={() => {
                            setActionError(null);
                            setPendingFeature({ plan, key: item.key, enabled: !enabled });
                          }}
                        >
                          {enabled ? tr('Desligar') : tr('Ligar')}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Card>
          );
        })}
      </div>

      {!resource.loading ? (
        <p className="nl-hint mt-4">
          {tr("Observação do contrato:")} <code>{tr("PATCH /api/admin/plans/:id")}</code> {tr("aceita preço mensal, desconto, limite, nome, descrição e situação — não existe campo para digitar o preço anual diretamente nem rota para criar ou excluir planos.")}
        </p>
      ) : null}

      <Modal
        open={pendingFeature !== null}
        onClose={() => (toggling ? undefined : setPendingFeature(null))}
        title={pendingFeature?.enabled ? 'Ligar recurso no plano' : 'Desligar recurso do plano'}
        footer={
          <>
            <Button variant="ghost" disabled={Boolean(toggling)} onClick={() => setPendingFeature(null)}>{tr("Voltar")}</Button>
            <Button
              variant={pendingFeature?.enabled ? 'primary' : 'danger'}
              loading={toggling !== null && pendingFeature !== null}
              disabled={toggling !== null}
              onClick={() => pendingFeature && void toggleFeature(pendingFeature.plan, pendingFeature.key, pendingFeature.enabled)}
            >
              {tr("Confirmar")}
            </Button>
          </>
        }
      >
        {pendingFeature ? (
          <Alert tone="warning" title={tr("Efeito imediato")}>
            {tr("O recurso")} <strong>{tr(FEATURE_LABEL[pendingFeature.key] ?? pendingFeature.key)}</strong> {tr("será")}{' '}
            <strong>{pendingFeature.enabled ? tr('liberado') : tr('bloqueado')}</strong> {tr("para todos os estabelecimentos assinantes do plano")}{' '}
            <strong>{pendingFeature.plan.name}</strong>{tr(". Quem tentar usar o recurso bloqueado recebe erro")}{' '}
            <code>{tr("feature_not_in_plan")}</code> {tr("e precisa fazer upgrade. A mudança é registrada em Auditoria (")}<code>{tr("admin.plan_feature_toggled")}</code>).
          </Alert>
        ) : null}
      </Modal>

      <Modal
        open={pendingDeactivate !== null}
        onClose={() => setPendingDeactivate(null)}
        title={tr("Desativar plano")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingDeactivate(null)}>{tr("Manter ativo")}</Button>
            <Button
              variant="danger"
              onClick={() => {
                if (pendingDeactivate) patchDraft(pendingDeactivate, { active: false });
                setPendingDeactivate(null);
              }}
            >
              {tr("Desativar")}
            </Button>
          </>
        }
      >
        {pendingDeactivate ? (
          <Alert tone="danger" title={tr("Consequência")}>
            <strong>{pendingDeactivate.name}</strong> {tr("deixa de aparecer em /precos e não pode ser escolhido em novas assinaturas ou upgrades. A alteração ainda não foi gravada: clique em “Salvar plano” para enviá-la ao backend.")}
          </Alert>
        ) : null}
      </Modal>
    </AdminScreen>
  );
}

function EmptyPlans(): JSX.Element {
  return (
    <Card>
      <h2 className="text-xl">{tr("Nenhum plano cadastrado")}</h2>
      <p className="nl-hint mt-1">
        {tr("O backend não expõe rota de criação de planos — execute o seed da API (")}<code>{tr("npm run seed")}</code> {tr("em apps/api) para criar Start, Pro e Business antes de editar preços e recursos.")}
      </p>
    </Card>
  );
}
